import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import type { FormObserver } from '../types/block';
import { ActionRegistry } from '@packages/table-builder/registry';
import { evaluateCondition } from '../evaluators/conditionEvaluator';
import type { ConditionContext } from '../evaluators/conditionEvaluator';
import {
  globalCache,
  globalDedup,
  buildCacheKey,
  IMMUTABLE_POLICY,
  SHORT_LIVED_POLICY,
  DEFAULT_POLICY,
} from '@packages/cache-manager';
import type { CachePolicy } from '@packages/cache-manager';
import {
  markProgrammaticWrites,
  type ProgrammaticWriteGuardRef,
} from './programmaticWriteGuard';
import type { FormPrecisionConfig } from '../precision/precisionContract';
import { normalizeProgrammaticNumericValue } from '../precision/precisionContract';

interface UseObserverProcessorParams {
  form: UseFormReturn<Record<string, unknown>>;
  observers: FormObserver[];
  meta: Record<string, unknown>;
  getOptionProp: (field: string, prop: string) => unknown;
  customResolvers?: Record<string, (ctx: ConditionContext) => boolean>;
  hydratingRef?: React.RefObject<boolean>;
  hydrationEpochRef?: React.RefObject<number>;
  programmaticWriteGuard?: ProgrammaticWriteGuardRef;
  precisionConfig?: FormPrecisionConfig;
}

/** Debounce/abort key — multiple observers may share the same `watch` field. */
export function getObserverInstanceKey(observer: FormObserver): string {
  const key = observer.id ?? observer.lookup.actionId;
  if (!key) {
    if (import.meta.env?.DEV) {
      console.warn('[form-engine] Observer missing id and lookup.actionId; falling back to watch', observer.watch);
    }
    return observer.watch;
  }
  return key;
}

export function useObserverProcessor({
  form,
  observers,
  meta,
  getOptionProp,
  customResolvers,
  hydratingRef,
  hydrationEpochRef,
  programmaticWriteGuard,
  precisionConfig,
}: UseObserverProcessorParams): void {
  const watchFields = useMemo(() => observers.map((o) => {
    if (Array.isArray(o.watch)) return o.watch[0] ?? '';
    return o.watch ?? '';
  }).filter(Boolean), [observers]);
  const watchedValues = useWatch({ control: form.control, name: watchFields, disabled: watchFields.length === 0 });
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const prevValuesRef = useRef<Record<string, unknown>>({});
  const prevEpochRef = useRef(hydrationEpochRef?.current ?? 0);
  const isInitialRef = useRef(true);

  /** lifecycleProcessor bumps hydrationEpochRef in rAF after form.reset(); sync prevEpochRef so the next user edit is not misclassified as hydration. */
  const scheduleHydrationEpochSync = useCallback(() => {
    if (!hydrationEpochRef) return;
    requestAnimationFrame(() => {
      prevEpochRef.current = hydrationEpochRef.current ?? 0;
    });
  }, [hydrationEpochRef]);

  const executeObserver = useCallback(
    async (observer: FormObserver, value: unknown) => {
      const instanceKey = getObserverInstanceKey(observer);
      const existingController = abortControllersRef.current.get(instanceKey);
      if (existingController) {
        existingController.abort();
      }

      const controller = new AbortController();
      abortControllersRef.current.set(instanceKey, controller);

      const currentValues = form.getValues();
      const ctx: ConditionContext = {
        fieldValue: value,
        values: currentValues,
        meta,
        getOptionProp,
        customResolvers,
      };

      if (observer.condition && !evaluateCondition(observer.condition, ctx)) {
        return;
      }

      const params: Record<string, unknown> = {};
      if (observer.lookup.paramField) {
        params[observer.lookup.paramField] = value;
      }
      if (observer.lookup.params) {
        for (const [key, path] of Object.entries(observer.lookup.params)) {
          const resolved = getNestedValue(currentValues, path);
          if (resolved !== undefined) {
            params[key] = resolved;
          } else {
            params[key] = stripRepeatablePrefix(path);
          }
        }
      }

      try {
        const cacheKey = buildCacheKey(observer.lookup.actionId, params as Record<string, string>);
        let result: unknown;

        // RACE CONDITION GUARD: snapshot target fields before async call
        const rejectIfTouched = observer.lookup.rejectIfTouched !== false;
        const targetSnapshot: Record<string, unknown> = {};
        if (rejectIfTouched) {
          for (const formField of Object.values(observer.mapTo)) {
            targetSnapshot[formField] = form.getValues(formField);
          }
        }

        if (observer.lookup.cache) {
          const policy = resolveCachePolicy(observer.lookup.cache);
          const cached = globalCache.get(cacheKey);

          if (cached.status === 'fresh') {
            result = cached.data;
          } else {
            const fetched = await globalDedup.dedupe(cacheKey, () =>
              ActionRegistry.executeData(observer.lookup.actionId, params as Record<string, string>)
            );
            if (controller.signal.aborted) return;
            globalCache.set(cacheKey, fetched, policy);
            result = fetched;
          }
        } else {
          result = await ActionRegistry.executeData(observer.lookup.actionId, params as Record<string, string>);
        }

        if (controller.signal.aborted) return;

        // Guard 3: Reaction window — delay before writing, abortable if user re-triggers.
        const reactionMs = observer.lookup.reactionWindowMs ?? 0;
        if (reactionMs > 0) {
          await new Promise<void>((resolve, reject) => {
            const timer = setTimeout(resolve, reactionMs);
            controller.signal.addEventListener('abort', () => {
              clearTimeout(timer);
              reject(controller.signal.reason);
            }, { once: true });
          });
          if (controller.signal.aborted) return;
        }

        if (result && typeof result === 'object') {
          const pendingWrites: Array<{ formField: string; val: unknown }> = [];

          for (const [resultKey, formField] of Object.entries(observer.mapTo)) {
            let val = (result as Record<string, unknown>)[resultKey];
            if (val === undefined) continue;
            if (val === null && observer.lookup.skipNull) continue;

            if (rejectIfTouched) {
              const currentVal = form.getValues(formField);
              const snapshotVal = targetSnapshot[formField];
              if (currentVal !== snapshotVal) {
                continue;
              }
            }

            if (observer.formatters?.[resultKey]) {
              val = applyFormatter(val, observer.formatters[resultKey]);
            }
            pendingWrites.push({ formField, val });
          }

          if (pendingWrites.length > 0) {
            markProgrammaticWrites(
              programmaticWriteGuard,
              pendingWrites.map((w) => w.formField),
            );
          }

          const shouldTriggerWatch = observer.cascade === true;
          for (const { formField, val } of pendingWrites) {
            const normalized = normalizeProgrammaticNumericValue(val, formField, precisionConfig);
            form.setValue(formField, normalized, {
              shouldDirty: shouldTriggerWatch,
              shouldValidate: shouldTriggerWatch,
            });
          }
        }
      } catch (err) {
        if (controller.signal.aborted) return;

        if (observer.errorField) {
          const errorMsg = err instanceof Error ? err.message : 'Lookup failed';
          form.setValue(observer.errorField, errorMsg, { shouldDirty: false, shouldValidate: false });
        } else {
          console.warn(`[form-engine] Observer lookup failed for ${observer.lookup.actionId}:`, err);
        }
      }
    },
    [form, meta, getOptionProp, customResolvers, programmaticWriteGuard, precisionConfig],
  );

  useEffect(() => {
    const watchMap: Record<string, unknown> = {};
    watchFields.forEach((field, idx) => {
      watchMap[field] = Array.isArray(watchedValues) ? watchedValues[idx] : watchedValues;
    });

    if (isInitialRef.current) {
      isInitialRef.current = false;
      prevValuesRef.current = { ...watchMap };

      const runOnMountObservers = observers.filter((o) => o.runOnMount);
      for (const observer of runOnMountObservers) {
        const value = watchMap[observer.watch];
        if (value != null && value !== '') {
          const debounceMs = observer.debounceMs ?? 300;
          const instanceKey = getObserverInstanceKey(observer);
          const timeout = setTimeout(() => {
            executeObserver(observer, value);
          }, debounceMs);
          timeoutsRef.current.set(`mount_${instanceKey}`, timeout);
        }
      }
      return;
    }

    if (hydratingRef?.current) {
      prevValuesRef.current = { ...watchMap };
      scheduleHydrationEpochSync();
      return;
    }

    if (hydrationEpochRef && hydrationEpochRef.current !== prevEpochRef.current) {
      prevValuesRef.current = { ...watchMap };
      prevEpochRef.current = hydrationEpochRef.current;
      return;
    }

    for (let i = 0; i < observers.length; i++) {
      const observer = observers[i];
      const value = watchMap[observer.watch];

      if (value === prevValuesRef.current[observer.watch]) continue;

      const instanceKey = getObserverInstanceKey(observer);
      const existingTimeout = timeoutsRef.current.get(instanceKey);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      const debounceMs = observer.debounceMs ?? 300;
      const timeout = setTimeout(() => {
        executeObserver(observer, value);
      }, debounceMs);

      timeoutsRef.current.set(instanceKey, timeout);
    }

    prevValuesRef.current = { ...watchMap };
  }, [watchedValues]);

  useEffect(() => {
    return () => {
      for (const timeout of timeoutsRef.current.values()) {
        clearTimeout(timeout);
      }
      timeoutsRef.current.clear();

      for (const controller of abortControllersRef.current.values()) {
        controller.abort();
      }
      abortControllersRef.current.clear();
    };
  }, []);
}

function applyFormatter(value: unknown, formatter: string): unknown {
  if (typeof value !== 'string') return value;

  switch (formatter) {
    case 'capitalize':
      return value.replace(/\b\w/g, (c) => c.toUpperCase());
    case 'uppercase':
      return value.toUpperCase();
    case 'lowercase':
      return value.toLowerCase();
    case 'trim':
      return value.trim();
    default:
      return value;
  }
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

const REPEATABLE_PREFIX_RE = /^[^.]+\.\d+\./;

/**
 * Strip a repeatable-mode prefix (`items.0.`, `documents.3.`, etc.) from a
 * path string. When `getNestedValue` fails to resolve a prefixed param, this
 * recovers the original value — whether it was a literal constant like `"PSU"`
 * or a nested field path like `work.address.city`.
 */
function stripRepeatablePrefix(path: string): string {
  return REPEATABLE_PREFIX_RE.test(path) ? path.replace(REPEATABLE_PREFIX_RE, '') : path;
}

function resolveCachePolicy(
  cache: 'immutable' | 'short-lived' | 'default' | { staleTime: number; gcTime: number },
): Partial<CachePolicy> {
  if (typeof cache === 'object') return cache;
  switch (cache) {
    case 'immutable':
      return IMMUTABLE_POLICY;
    case 'short-lived':
      return SHORT_LIVED_POLICY;
    case 'default':
      return DEFAULT_POLICY;
  }
}
